import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import useServiceNotesService from '../../services/useServiceNotesService';
import ServiceNoteForm from './forms/ServiceNoteForm';
import { ServiceNoteCreate } from '../../models/ServiceNoteModel';
import type { ServiceResponse } from '../../hooks/useFetch';
import './styles/ServiceNoteModal.css';

interface ServiceNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceId: number;
  onSuccess?: () => void;
}

const ServiceNoteModal: React.FC<ServiceNoteModalProps> = ({
  isOpen,
  onClose,
  serviceId,
  onSuccess,
}) => {
  const { store, fetching } = useServiceNotesService();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSubmit = (payload: ServiceNoteCreate) => {
    store(serviceId, payload, {
      onSuccess: (r: ServiceResponse) => {
        if (r?.data) {
          onSuccess?.();
          onClose();
        }
      },
      onError: (r: ServiceResponse) => {
        console.error('Error al crear nota:', r);
      },
    });
  };

  if (!isOpen) return null;

  const content = (
    <div className="service-note-overlay" onClick={onClose}>
      <div
        className="service-note-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="service-note-header">
          <h3 className="service-note-title">Nueva nota del servicio</h3>
          <button
            type="button"
            className="service-note-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X size={22} />
          </button>
        </div>
        <ServiceNoteForm
          onSubmit={handleSubmit}
          onCancel={onClose}
          loading={fetching.store}
          submitLabel="Agregar nota"
          cancelLabel="Cancelar"
        />
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default ServiceNoteModal;
